import { useRef, useState, useEffect } from "react";
import { type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { format, startOfDay } from "date-fns";
import {
  CalendarIcon,
  User,
  DollarSign,
  Clock,
  CheckCircle2,
  AlertCircle,
  Building,
  FileText,
  ClipboardCheck,
  ArrowLeft,
  Save,
  Pencil,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { PropertyTooltip } from "@/components/shared/PropertyTooltip";
import {
  SelectFormField,
  SelectOption,
} from "@/components/shared/Form/SelectFormField";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { LeaseHeader } from "./LeaseHeader";
import { useLeaseForm, type LeaseFormValues } from "../hooks/useLeaseForm";
import { PageHeader } from "@/components/shared/PageHeader";

export enum LeaseFormMode {
  VIEW = "view",
  CREATE = "create",
  EDIT = "edit"
}

interface LeaseFormProps {
  initialData?: Partial<LeaseFormValues>;
  onSubmit: (data: LeaseFormValues) => Promise<void>;
  readOnly?: boolean;
  isLoading?: boolean;
  initialMode?: LeaseFormMode;
  onBack: () => void;
}

const LeaseForm = ({
  initialData,
  onSubmit,
  readOnly = false,
  isLoading = false,
  initialMode = LeaseFormMode.VIEW,
  onBack,
}: LeaseFormProps) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<LeaseFormMode>(initialMode);

  const isFormReadOnly = readOnly || mode === LeaseFormMode.VIEW;

  const { form, fetchProperties, fetchTenants, fetchManagers, isEditMode } = useLeaseForm({
    initialData,
    readOnly: isFormReadOnly,
  });

  const startDate = form.watch("startDate");

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  const handleDateSelect = (
    date: Date | undefined,
    field: { onChange: (value: Date | null) => void },
  ) => {
    field.onChange(date || null);
  };

  const statusOptions = [
    {
      value: "ACTIVE",
      label: t("leases.status.ACTIVE"),
      icon: <CheckCircle2 className="h-4 w-4" />,
      color: "green",
      bgColor: "bg-green-100 dark:bg-green-900/20",
    },
    {
      value: "PENDING",
      label: t("leases.status.PENDING"),
      icon: <Clock className="h-4 w-4" />,
      color: "yellow",
      bgColor: "bg-yellow-100 dark:bg-yellow-900/20",
    },
    {
      value: "COMPLETED",
      label: t("leases.status.COMPLETED"),
      icon: <ClipboardCheck className="h-4 w-4" />,
      color: "blue",
      bgColor: "bg-blue-100 dark:bg-blue-900/20",
    },
    {
      value: "CANCELED",
      label: t("leases.status.CANCELED"),
      icon: <AlertCircle className="h-4 w-4" />,
      color: "red",
      bgColor: "bg-red-100 dark:bg-red-900/20",
    },
  ];

  const handleEdit = () => {
    setMode(LeaseFormMode.EDIT);
  };

  const handleCancelEdit = () => {
    if (initialData) {
      form.reset(initialData);
    }
    setMode(LeaseFormMode.VIEW);
  };

  const handleFormSubmit: SubmitHandler<LeaseFormValues> = async (data) => {
    try {
      const currentValues = { ...data };

      await onSubmit(data);

      if (mode === LeaseFormMode.EDIT) {
        form.reset(currentValues);
        setMode(LeaseFormMode.VIEW);
      }
    } catch (error) {

    }
  };

  const getHeaderTitle = () => {
    switch (mode) {
      case LeaseFormMode.VIEW:
        return t("leases.view.title");
      case LeaseFormMode.CREATE:
        return t("leases.create.title");
      case LeaseFormMode.EDIT:
        return t("leases.edit.title");
      default:
        return "";
    }
  };

  const getHeaderDescription = () => {
    switch (mode) {
      case LeaseFormMode.VIEW:
        return t("leases.view.description");
      case LeaseFormMode.CREATE:
        return t("leases.create.description");
      case LeaseFormMode.EDIT:
        return t("leases.edit.description");
      default:
        return "";
    }
  };

  const renderHeaderActions = () => {
    switch (mode) {
      case LeaseFormMode.VIEW:
        return (
          <div className="flex gap-2">
            <Button variant="outline" onClick={onBack} type="button">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("common.back")}
            </Button>
            <Button variant="outline" onClick={handleEdit} type="button">
              <Pencil className="mr-2 h-4 w-4" />
              {t("common.edit")}
            </Button>
          </div>
        );
      case LeaseFormMode.CREATE:
        return (
          <div className="flex gap-2">
            <Button variant="outline" onClick={onBack} type="button">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("common.back")}
            </Button>
            <Button type="submit" onClick={() => form.handleSubmit(handleFormSubmit)()}>
              <Save className="mr-2 h-4 w-4" />
              {t("common.save")}
            </Button>
          </div>
        );
      case LeaseFormMode.EDIT:
        return (
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleCancelEdit} type="button">
              <X className="mr-2 h-4 w-4" />
              {t("common.cancel")}
            </Button>
            <Button type="submit" onClick={() => form.handleSubmit(handleFormSubmit)()}>
              <Save className="mr-2 h-4 w-4" />
              {t("common.save")}
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-8">
      <PageHeader
        icon={<FileText />}
        title={getHeaderTitle()}
        description={getHeaderDescription()}
        actions={renderHeaderActions()}
      />
      <form onSubmit={form.handleSubmit(handleFormSubmit)}>
        <Form {...form}>
          <div className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                <Card className="border shadow-sm">
                  <CardContent className="p-6">
                    <div className="space-y-8">
                      <section>
                        <div className="flex items-center gap-2 mb-6">
                          <Building className="h-5 w-5 text-primary" />
                          <h3 className="text-lg font-medium">
                            {t("leases.form.propertySection")}
                          </h3>
                        </div>
                        <SelectFormField
                          name="propertyUUID"
                          control={form.control}
                          label={t("leases.form.property")}
                          placeholder={t("leases.form.selectProperty")}
                          disabled={isEditMode || isFormReadOnly}
                          queryKey={["properties"]}
                          queryFn={fetchProperties}
                          clearable
                          renderOption={(option) => (
                            <PropertyTooltip
                              property={{
                                propertyUUID: option.uuid,
                                name: option.name,
                                address: option.address,
                                city: option.city,
                                type: (option.type?.toLowerCase() ||
                                  "apartment") as
                                  | "apartment"
                                  | "house"
                                  | "office"
                                  | "commercial"
                                  | "land",
                                price: option.monthlyPrice || 0,
                                currency: "USD",
                                status: "available",
                                ownerUUID: "00000000-0000-0000-0000-000000000000",
                                isActive: true,
                                rooms: option.rooms,
                                area: option.totalArea,
                                createdAt: new Date().toISOString(),
                                updatedAt: new Date().toISOString(),
                              }}
                              size="xs"
                              showDetails={false}
                              disableNavigation
                            />
                          )}
                        />
                      </section>

                      <Separator />

                      <section>
                        <div className="flex items-center gap-2 mb-6">
                          <User className="h-5 w-5 text-primary" />
                          <h3 className="text-lg font-medium">
                            {t("leases.form.tenantSection")}
                          </h3>
                        </div>
                        <SelectFormField
                          name="tenantUUID"
                          control={form.control}
                          label={t("leases.form.tenant")}
                          placeholder={t("leases.form.selectTenant")}
                          disabled={isFormReadOnly}
                          queryKey={["tenants"]}
                          queryFn={fetchTenants}
                          clearable
                          renderOption={(option) => (
                            <div className="flex items-center gap-3">
                              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <User className="h-4 w-4 text-primary" />
                              </div>
                              <div>
                                <div className="font-medium">
                                  {`${option.firstName} ${option.lastName}`}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {option.email}
                                </div>
                              </div>
                            </div>
                          )}
                        />
                      </section>

                      <Separator />

                      <section>
                        <div className="flex items-center gap-2 mb-6">
                          <User className="h-5 w-5 text-primary" />
                          <h3 className="text-lg font-medium">{t("users.title")}</h3>
                        </div>
                        <SelectFormField
                          name="managerUUID"
                          control={form.control}
                          label={t("leases.form.manager")}
                          placeholder={t("leases.form.managerPlaceholder")}
                          disabled={isFormReadOnly}
                          queryKey={["managers"]}
                          queryFn={fetchManagers}
                          clearable
                          renderOption={(option) => (
                            <div className="flex items-center gap-3">
                              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <User className="h-4 w-4 text-primary" />
                              </div>
                              <div>
                                <div className="font-medium">
                                  {`${option.firstName} ${option.lastName}`}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {option.email}
                                </div>
                              </div>
                            </div>
                          )}
                        />
                      </section>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border shadow-sm">
                  <CardContent className="p-6">
                    <section>
                      <div className="flex items-center gap-2 mb-6">
                        <DollarSign className="h-5 w-5 text-primary" />
                        <h3 className="text-lg font-medium">
                          {t("leases.form.financialSection")}
                        </h3>
                      </div>
                      <div className="grid gap-8 grid-cols-1 md:grid-cols-3">
                        <FormField
                          control={form.control}
                          name="monthlyRent"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <DollarSign className="h-4 w-4 text-primary" />
                                {t("leases.form.monthlyRent")}
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                  disabled={isFormReadOnly}
                                  className={cn(
                                    isFormReadOnly && "opacity-50 cursor-not-allowed"
                                  )}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="securityDeposit"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <DollarSign className="h-4 w-4 text-primary" />
                                {t("leases.form.deposit")}
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                  disabled={isFormReadOnly}
                                  className={cn(
                                    isFormReadOnly && "opacity-50 cursor-not-allowed"
                                  )}
                                />
                              </FormControl>
                              <FormDescription>
                                {t("leases.form.depositOptional")}
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="paymentDay"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-primary" />
                                {t("leases.form.paymentDay")}
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  max={31}
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(Number(e.target.value))
                                  }
                                  disabled={isFormReadOnly}
                                  className={cn(
                                    isFormReadOnly && "opacity-50 cursor-not-allowed"
                                  )}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </section>
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-8">
                <Card className="border shadow-sm">
                  <CardContent className="p-6">
                    <section>
                      <div className="flex items-center gap-2 mb-6">
                        <CalendarIcon className="h-5 w-5 text-primary" />
                        <h3 className="text-lg font-medium">
                          {t("leases.form.durationSection")}
                        </h3>
                      </div>

                      <div className="space-y-6">
                        <FormField
                          control={form.control}
                          name="startDate"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t("leases.form.startDate")}</FormLabel>
                              <Popover>
                                <PopoverTrigger asChild disabled={isFormReadOnly}>
                                  <FormControl>
                                    <Button
                                      variant="outline"
                                      className={cn(
                                        "w-full pl-3 text-left font-normal",
                                        !field.value && "text-muted-foreground",
                                        isFormReadOnly && "opacity-50 cursor-not-allowed"
                                      )}
                                      disabled={isFormReadOnly}
                                    >
                                      {field.value ? (
                                        format(field.value, "PPP")
                                      ) : (
                                        <span>{t("leases.form.pickDate")}</span>
                                      )}
                                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                    </Button>
                                  </FormControl>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-auto p-0"
                                  align="start"
                                >
                                  <Calendar
                                    mode="single"
                                    selected={field.value || undefined}
                                    onSelect={(date) =>
                                      handleDateSelect(date, field)
                                    }
                                    disabled={{ before: startOfDay(new Date()) }}
                                    initialFocus
                                  />
                                </PopoverContent>
                              </Popover>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="endDate"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t("leases.form.endDate")}</FormLabel>
                              <Popover>
                                <PopoverTrigger asChild disabled={isFormReadOnly}>
                                  <FormControl>
                                    <Button
                                      variant="outline"
                                      className={cn(
                                        "w-full pl-3 text-left font-normal",
                                        !field.value && "text-muted-foreground",
                                        isFormReadOnly && "opacity-50 cursor-not-allowed"
                                      )}
                                      disabled={isFormReadOnly}
                                    >
                                      {field.value ? (
                                        format(field.value, "PPP")
                                      ) : (
                                        <span>{t("leases.form.pickDate")}</span>
                                      )}
                                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                    </Button>
                                  </FormControl>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-auto p-0"
                                  align="start"
                                >
                                  <Calendar
                                    mode="single"
                                    selected={field.value || undefined}
                                    onSelect={(date) =>
                                      handleDateSelect(date, field)
                                    }
                                    disabled={{
                                      before: startDate || startOfDay(new Date()),
                                    }}
                                    initialFocus
                                  />
                                </PopoverContent>
                              </Popover>
                              <FormDescription>
                                {t("leases.form.endDateOptional")}
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="autoRenew"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                              <div className="space-y-0.5">
                                <FormLabel className="text-base">
                                  {t("leases.form.autoRenew")}
                                </FormLabel>
                                <FormDescription>
                                  {t("leases.form.autoRenewDescription")}
                                </FormDescription>
                              </div>
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  disabled={isFormReadOnly}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    </section>
                  </CardContent>
                </Card>

                <Card className="border shadow-sm">
                  <CardContent className="p-6">
                    <section>
                      <div className="flex items-center gap-2 mb-6">
                        <AlertCircle className="h-5 w-5 text-primary" />
                        <h3 className="text-lg font-medium">
                          {t("leases.form.statusSection")}
                        </h3>
                      </div>
                      <FormField
                        control={form.control}
                        name="status"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("leases.form.status")}</FormLabel>
                            <Select
                              value={field.value}
                              onValueChange={field.onChange}
                              disabled={isFormReadOnly}
                            >
                              <FormControl>
                                <SelectTrigger
                                  className={cn(
                                    isFormReadOnly && "opacity-50 cursor-not-allowed"
                                  )}
                                >
                                  <SelectValue
                                    placeholder={t("leases.form.selectStatus")}
                                  >
                                    {field.value && (
                                      <div className="flex items-center gap-2">
                                        {
                                          statusOptions.find(
                                            (s) => s.value === field.value
                                          )?.icon
                                        }
                                        <span>
                                          {
                                            statusOptions.find(
                                              (s) => s.value === field.value
                                            )?.label
                                          }
                                        </span>
                                      </div>
                                    )}
                                  </SelectValue>
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {statusOptions.map((status) => (
                                  <SelectItem
                                    key={status.value}
                                    value={status.value}
                                    className="flex items-center gap-2"
                                  >
                                    <div
                                      className={cn(
                                        "flex items-center gap-2 rounded-md px-1 py-0.5",
                                        status.bgColor
                                      )}
                                    >
                                      {status.icon}
                                      <span>{status.label}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </section>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </Form>
      </form>
    </div>
  );
};

export default LeaseForm;
